/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BalanceSourceTypeEnum } from '../models/BalanceSourceTypeEnum';
import type { CheckSignerResponse } from '../models/CheckSignerResponse';
import type { DatabaseWithRowsAndColumns } from '../models/DatabaseWithRowsAndColumns';
import type { EntitySigningSummary } from '../models/EntitySigningSummary';
import type { FinancialInstitutionWithAssetAccounts } from '../models/FinancialInstitutionWithAssetAccounts';
import type { GenerateSigningLinkPayload } from '../models/GenerateSigningLinkPayload';
import type { GenerateSigningLinkResponse } from '../models/GenerateSigningLinkResponse';
import type { MySigningAssignmentsResponse } from '../models/MySigningAssignmentsResponse';
import type { RevokeSigningTokensPayload } from '../models/RevokeSigningTokensPayload';
import type { SigningSignPayload } from '../models/SigningSignPayload';
import type { SigningStatusResponse } from '../models/SigningStatusResponse';
import type { SigningTokenSummary } from '../models/SigningTokenSummary';
import type { ValidateSigningTokenResponse } from '../models/ValidateSigningTokenResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class SigningService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Generate Signing Link
     * @param taskId
     * @param requestBody
     * @returns GenerateSigningLinkResponse Successful Response
     * @throws ApiError
     */
    public generateSigningLink(
        taskId: string,
        requestBody: GenerateSigningLinkPayload,
    ): CancelablePromise<GenerateSigningLinkResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/signing/tasks/{task_id}/generate-link',
            path: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Revoke Signing Tokens
     * @param taskId
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public revokeSigningTokens(
        taskId: string,
        requestBody?: (RevokeSigningTokensPayload | null),
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/signing/tasks/{task_id}/tokens',
            path: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Signing Tokens For Task
     * @param taskId
     * @returns SigningTokenSummary Successful Response
     * @throws ApiError
     */
    public listSigningTokensForTask(
        taskId: string,
    ): CancelablePromise<Array<SigningTokenSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/tasks/{task_id}/tokens',
            path: {
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Signing Status
     * @param taskId
     * @returns SigningStatusResponse Successful Response
     * @throws ApiError
     */
    public getSigningStatus(
        taskId: string,
    ): CancelablePromise<SigningStatusResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/tasks/{task_id}/status',
            path: {
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Check Signer
     * @param taskId
     * @param email
     * @returns CheckSignerResponse Successful Response
     * @throws ApiError
     */
    public checkSigner(
        taskId: string,
        email: string,
    ): CancelablePromise<CheckSignerResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/tasks/{task_id}/check-signer',
            path: {
                'task_id': taskId,
            },
            query: {
                'email': email,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get My Signing Assignments
     * @param taskId
     * @returns MySigningAssignmentsResponse Successful Response
     * @throws ApiError
     */
    public getMySigningAssignments(
        taskId: string,
    ): CancelablePromise<MySigningAssignmentsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/tasks/{task_id}/my-assignments',
            path: {
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Sign Direct
     * @param taskId
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public signDirect(
        taskId: string,
        requestBody: SigningSignPayload,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/signing/tasks/{task_id}/sign-direct',
            path: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Validate Signing Token
     * @param xSigningToken
     * @returns ValidateSigningTokenResponse Successful Response
     * @throws ApiError
     */
    public validateSigningToken(
        xSigningToken: string,
    ): CancelablePromise<ValidateSigningTokenResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/validate',
            headers: {
                'x-signing-token': xSigningToken,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Sign Via Signing
     * @param xSigningToken
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public signViaSigning(
        xSigningToken: string,
        requestBody: SigningSignPayload,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/signing/sign',
            headers: {
                'x-signing-token': xSigningToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Entity For Signing
     * @param entityId
     * @param xSigningToken
     * @returns EntitySigningSummary Successful Response
     * @throws ApiError
     */
    public getEntityForSigning(
        entityId: string,
        xSigningToken: string,
    ): CancelablePromise<EntitySigningSummary> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/entity-data/{entity_id}/details',
            path: {
                'entity_id': entityId,
            },
            headers: {
                'x-signing-token': xSigningToken,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Institutions For Signing
     * @param entityId
     * @param xSigningToken
     * @param ignoreArchived
     * @param asOf
     * @param sourceType
     * @returns FinancialInstitutionWithAssetAccounts Successful Response
     * @throws ApiError
     */
    public listInstitutionsForSigning(
        entityId: string,
        xSigningToken: string,
        ignoreArchived?: (boolean | null),
        asOf?: (string | null),
        sourceType?: (BalanceSourceTypeEnum | null),
    ): CancelablePromise<Array<FinancialInstitutionWithAssetAccounts>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/entity-data/{entity_id}/institutions-with-asset-accounts',
            path: {
                'entity_id': entityId,
            },
            headers: {
                'x-signing-token': xSigningToken,
            },
            query: {
                'ignore_archived': ignoreArchived,
                'as_of': asOf,
                'source_type': sourceType,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Database For Signing
     * @param entityId
     * @param databaseId
     * @param xSigningToken
     * @returns DatabaseWithRowsAndColumns Successful Response
     * @throws ApiError
     */
    public getDatabaseForSigning(
        entityId: string,
        databaseId: string,
        xSigningToken: string,
    ): CancelablePromise<DatabaseWithRowsAndColumns> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/signing/entity-data/{entity_id}/database/{database_id}',
            path: {
                'entity_id': entityId,
                'database_id': databaseId,
            },
            headers: {
                'x-signing-token': xSigningToken,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
